/**
 * Printing, as the application uses it.
 *
 * WHY A SCREEN MAY NOT TOUCH THE PORT DIRECTLY
 * -------------------------------------------
 * An ESLint rule forbids `features/` from importing `@platform/*`, and it is
 * right. A screen that imported `createPrintPort` would be free to construct
 * its own adapter, free to pick the wrong one, and free to forget the font
 * guard — and the first two would look identical to correct code in review.
 *
 * So the port is owned here. A screen says "print this document" and gets an
 * answer; it does not know or care whether that is an iframe and `window.print`
 * or a preload bridge, and it cannot accidentally bypass the capabilities it
 * was supposed to respect.
 */
import { printableA4, printableThermal } from '@platform/print/printPort';
import { createPrintPort } from '@platform/print/createPrintPort';
import type { PrintCapabilities } from '@platform/print/printPort';
import type { NormalizedDocument } from '@core/doc/types';
import type { BusinessIdentity } from '@core/doc/renderA4';
import type { RollWidth } from '@core/doc/thermal';

/** What this platform can actually do. The UI asks; it does not guess. */
export function printCapabilities(): PrintCapabilities {
  return createPrintPort().capabilities;
}

export interface PrintableResult {
  readonly printed: boolean;
}

/** Print one document on A4. Refuses if the fonts did not load. */
export async function printDocument(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  assetsBaseUrl = '',
): Promise<PrintableResult> {
  return createPrintPort().print({ document: printableA4(m, biz, assetsBaseUrl) });
}

/** Print one document on a roll. */
export async function printDocumentToRoll(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  opt: { mediaWidthMm: RollWidth; printableWidthMm?: number; copies?: number; assetsBaseUrl?: string },
): Promise<PrintableResult> {
  return createPrintPort().print({
    document: printableThermal(m, biz, opt),
    copies: opt.copies,
  });
}

export interface SavePdfResult {
  readonly saved: boolean;
  readonly filePath?: string;
}

/**
 * Save a PDF, if this platform can.
 *
 * @throws PrintPortError `PDF_UNAVAILABLE_ON_WEB` on the PWA. The UI is
 *         expected to have checked `printCapabilities().silentPdf` first; this
 *         is the second line of defence, not the first.
 */
export async function saveDocumentPdf(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  assetsBaseUrl = '',
): Promise<SavePdfResult> {
  return createPrintPort().savePdf({ document: printableA4(m, biz, assetsBaseUrl) });
}

export { PrintPortError } from '@platform/print/printPort';
export type { PrintCapabilities } from '@platform/print/printPort';
